import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DomaineDoesNotExist from "./DomaineDoesNotExist";
import { useCallback, useEffect, useState } from "react";
import { PsiCompetitors } from "../../../../../../logic/MarketPositioning/Scout/CompetitorsPageSpeed/CompetitorsPageSpeedEndPointLogic";
import { getDomain } from "tldts";
import { isWebUri } from "valid-url";
import tlds from "tlds";

interface CompetitorsPageScanModalProps {
  open: boolean;
  handleClose: () => void;
  data: any;
  serpDataLoading: boolean;
  userToken: string;
  selectedSiteUid: any;
  setRefresh: any;
}

const CompetitorsPageScanRequestsModal = ({
  open,
  handleClose,
  data,
  serpDataLoading,
  userToken,
  selectedSiteUid,
  setRefresh,
}: CompetitorsPageScanModalProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [isDomainAvailable, setIsDomainAvailable] = useState(false);
  const [, setSharedUid] = useState<string | null>(null);
  const [, setWebsiteSharedUid] = useState<string | null>(null);
  const [postStatus, setPostStatus] = useState([]) as any;
  const [validDomain, setValidDomain] = useState([]) as any;
  const [showDomainNotExist, setShowDomainNotExist] = useState(false) as any;
  const [baseDomain, setBaseDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [urlError, setUrlError] = useState("");

  const extractDomain = (url: any) => {
    return getDomain(url);
  };

  const validateUrl = (value: any) => {
    // Allow completely empty values
    if (!value) {
      return "";
    }

    // Check if URL starts or ends with whitespace (spaces, tabs, newlines)
    if (value !== value.trim() || /^\s/.test(value) || /\s$/.test(value)) {
      return "URL cannot start or end with whitespace";
    }

    // If after trimming there's nothing left, it was all whitespace
    if (!value.trim()) {
      return "URL cannot be only whitespace";
    }

    const trimmedValue = value.trim();

    // Check if URL starts with special characters (excluding http/https protocol)
    if (
      !/^https?:\/\//.test(trimmedValue) &&
      /^[^a-zA-Z0-9]/.test(trimmedValue)
    ) {
      return "URL cannot start with special characters";
    }

    // Check if URL starts with http:// or https://
    if (!/^https?:\/\//.test(trimmedValue)) {
      return "URL must start with http:// or https://";
    }

    // Check for invalid characters including spaces anywhere in the URL
    if (/[\s]/.test(trimmedValue)) {
      return "URL cannot contain spaces";
    }

    // Validate using valid-url library
    const isValid = isWebUri(trimmedValue);
    if (!isValid) {
      return "Please enter a valid URL";
    }

    // Extract domain and validate TLD
    const baseDomain = getDomain(trimmedValue);
    if (!baseDomain) {
      return "URL must have a valid domain";
    }

    // Check if domain has a valid TLD
    const validTLDs = new Set(tlds);
    const hasValidTLD = [...validTLDs].some((tld) =>
      baseDomain.toLowerCase().endsWith(`.${tld}`),
    );

    if (!hasValidTLD) {
      return "URL must have a valid top-level domain";
    }

    // Check for proper domain structure (at least one dot after protocol)
    const domainPart = trimmedValue.replace(/^https?:\/\//, "");
    if (
      !domainPart.includes(".") ||
      domainPart.startsWith(".") ||
      domainPart.endsWith(".")
    ) {
      return "URL must have a valid domain";
    }

    return "";
  };

  // Function to check if searchValue matches any domain in the data array
  const checkDomainAvailability = async () => {
    setValidDomain(false);
    setPostStatus([]);
    setUrlError("");

    // Validate URL first
    const errorMessage = validateUrl(searchValue);
    if (errorMessage) {
      setValidDomain(true);
      setUrlError(errorMessage);
      return;
    }

    const baseDomain = extractDomain(searchValue.trim()) as any;

    if (!baseDomain) {
      setIsDomainAvailable(false);
      setValidDomain(true);
      setUrlError("Unable to extract domain from URL");
      return;
    }

    const foundDomain = data.find(
      (item: any) => item.domain.toLowerCase() === baseDomain.toLowerCase(),
    );

    setBaseDomain(baseDomain);

    const hasValidCompanyName =
      foundDomain?.company_name &&
      foundDomain.company_name.toLowerCase() !== "missing";
    if (foundDomain && hasValidCompanyName) {
      setLoading(true);
      let postData = {
        url_name: searchValue,
        serp_competitor_shared_uid: foundDomain.shared_uid,
      };

      setIsDomainAvailable(true);
      setSharedUid(foundDomain.shared_uid);

      const websiteSharedUid = selectedSiteUid;

      if (websiteSharedUid) {
        setWebsiteSharedUid(websiteSharedUid);
        await PsiCompetitors.postCompetitors(
          userToken,
          setPostStatus,
          websiteSharedUid,
          postData,
        );
      }
      setLoading(false);
    } else {
      setIsDomainAvailable(false);
      setShowDomainNotExist(true);
    }
  };

  const handleClosePopup = useCallback(() => {
    handleClose();
    setShowDomainNotExist(false);
    setSearchValue("");
    setPostStatus([]);
    setIsDomainAvailable(true);
    setValidDomain([]);
    setUrlError("");
    setRefresh(true);
  }, [handleClose, setRefresh]);

  const handleCloseDomainNotExistPopup = useCallback(() => {
    handleClose();
    setShowDomainNotExist(false);
    setSearchValue("");
    setPostStatus([]);
    setIsDomainAvailable(true);
    setValidDomain([]);
    setUrlError("");
  }, [handleClose]);

  const handleCancelPopup = () => {
    handleClose();
    setShowDomainNotExist(false);
    setSearchValue("");
    setPostStatus([]);
    setIsDomainAvailable(true);
    setValidDomain([]);
    setUrlError("");
  };

  // Automatically close modal after 1 second on success
  useEffect(() => {
    if (Object.keys(postStatus).length >= 1) {
      const timer = setTimeout(() => {
        handleClosePopup();
      }, 1000);

      return () => clearTimeout(timer); // Cleanup the timeout
    }
  }, [postStatus, handleClosePopup]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
    >
      <Paper sx={{ padding: "16px 24px 20px 24px" }}>
        <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
          Add Domain New Page Scan Request
        </DialogTitle>
        <TextField
          id="outlined-multiline-flexible"
          label="URL *"
          placeholder="Paste the URL of the page you would like to scan here"
          multiline
          color="secondary"
          size="small"
          fullWidth
          maxRows={4}
          value={searchValue}
          slotProps={{
            input: {
              endAdornment: serpDataLoading ? (
                <CircularProgress color="secondary" size={20} />
              ) : null,
            },
          }}
          onChange={(e) => {
            const value = e.target.value;
            setSearchValue(value);
            // Real-time validation - check for any non-empty value
            const errorMessage = validateUrl(value);
            if (errorMessage && value) {
              setValidDomain(true);
              setUrlError(errorMessage);
            } else {
              setValidDomain(false);
              setUrlError("");
            }
          }}
          disabled={isDomainAvailable === false && showDomainNotExist === true}
          error={validDomain === true}
          helperText={validDomain === true ? urlError : ""}
        />

        <Typography
          sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
        >
          Mandatory field
        </Typography>
        <Typography sx={{ fontSize: "12px", color: "#00000099" }}>
          New Page Scan Requests are created with a default active state.
        </Typography>

        {Object.keys(postStatus).length >= 1 && (
          <Alert
            sx={{ alignItems: "center", mt: 1 }}
            iconMapping={{
              success: <CheckCircleOutlineIcon fontSize="inherit" />,
            }}
          >
            Page Scan Requested successfully!
          </Alert>
        )}
        {validDomain === true && (
          <Alert severity="error" sx={{ alignItems: "center", mt: 1 }}>
            {urlError || "URL is not valid"}
          </Alert>
        )}
        {showDomainNotExist === true && (
          <DomaineDoesNotExist
            showDomainNotExist={showDomainNotExist}
            handleClosePopup={handleCloseDomainNotExistPopup}
            data={data}
            baseDomain={baseDomain}
            userToken={userToken}
            searchValue={searchValue}
            setPostStatus={setPostStatus}
            selectedSiteUid={selectedSiteUid}
            setRefresh={setRefresh}
          />
        )}
        {Object.keys(postStatus).length === 0 &&
          showDomainNotExist === false && (
            <Stack flexDirection={"row"} justifyContent={"end"} marginTop={2}>
              <Button
                color="secondary"
                onClick={handleCancelPopup}
                sx={{ mr: 1 }}
              >
                CANCEL
              </Button>
              <Button
                data-tracker="203"
                variant="contained"
                color="secondary"
                disabled={
                  !searchValue ||
                  validDomain === true ||
                  loading ||
                  serpDataLoading
                }
                onClick={checkDomainAvailability}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "REQUEST SCAN"
                )}
              </Button>
            </Stack>
          )}
      </Paper>
    </Dialog>
  );
};

export default CompetitorsPageScanRequestsModal;
